import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, taskAPI, visitAPI, phaseAPI } from '../lib/api';
import { formatDate, getStatusColor, getPhaseLabel } from '../lib/utils';
import { PhaseTracker } from '../components/PhaseTracker';
import {
  CheckSquare,
  Calendar,
  FileText,
  MessageSquare,
  Bell,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const ClientDashboard = () => {
  const { user } = useAuth();
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [phases, setPhases] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    if (!user?.client_id) {
      setLoading(false);
      return;
    }

    try {
      const [dashRes, phasesRes] = await Promise.all([
        dashboardAPI.getClientDashboard(user.client_id),
        phaseAPI.getAll({ client_id: user.client_id }),
      ]);
      setDashboardData(dashRes.data);
      setPhases(phasesRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user?.client_id) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          Your account is not linked to a client organization. Please contact admin.
        </p>
      </Card>
    );
  }

  const client = dashboardData?.client;
  const completedTasks = dashboardData?.completed_tasks || 0;
  const totalTasks = dashboardData?.total_tasks || 0;
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Calculate overall progress based on phases
  const completedPhases = phases.filter((p) => p.status === 'completed').length;
  const overallProgress = Math.round((completedPhases / 5) * 100);

  return (
    <div className="space-y-6 animate-in" data-testid="client-dashboard">
      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0f4c81] via-[#1a6cb4] to-[#2196F3] p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-blue-100 mb-4">
            {client?.organization_name} - NABH {client?.nabh_type?.replace('_', ' ')} Accreditation
          </p>
          <div className="flex items-center gap-4">
            <div>
              <p className="text-sm text-white/60">Current Phase</p>
              <Badge className="bg-white/20 text-white mt-1">
                {getPhaseLabel(client?.current_phase)}
              </Badge>
            </div>
            <div className="h-8 w-px bg-white/20" />
            <div>
              <p className="text-sm text-white/60">Overall Progress</p>
              <p className="text-2xl font-bold">{overallProgress}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50" data-testid="stat-pending-tasks">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <CheckSquare className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{dashboardData?.pending_tasks?.length || 0}</p>
              <p className="text-sm text-amber-500 font-medium">Pending Tasks</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="stat-upcoming-visits">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{dashboardData?.upcoming_visits?.length || 0}</p>
              <p className="text-sm text-blue-500 font-medium">Upcoming Visits</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="stat-completed-tasks">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <CheckSquare className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">{completedTasks}</p>
              <p className="text-sm text-emerald-500 font-medium">Completed Tasks</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-violet-50 to-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-violet-100 rounded-xl">
              <FileText className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-700">{completedPhases}/5</p>
              <p className="text-sm text-violet-500 font-medium">Phases Done</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Phase Progress */}
        <Card className="lg:col-span-2" data-testid="phase-progress-card">
          <CardHeader>
            <CardTitle className="text-lg">NABH Accreditation Progress</CardTitle>
            <CardDescription>Track your journey through all 5 phases</CardDescription>
          </CardHeader>
          <CardContent>
            <PhaseTracker
              phases={phases}
              currentPhase={client?.current_phase}
              onPhaseClick={(phase) => navigate(`/my-progress?phase=${phase.id}`)}
            />
          </CardContent>
        </Card>

        {/* Quick Actions & Notifications */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/documents')}
                data-testid="quick-documents-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Documents
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/tickets')}
                data-testid="quick-support-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Raise Support Ticket
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/invoices')}
                data-testid="quick-invoices-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Invoices
              </Button>
            </CardContent>
          </Card>

          {/* Task Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Task Completion</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span className="font-medium">{progressPercentage}%</span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
                <p className="text-sm text-muted-foreground">
                  {completedTasks} of {totalTasks} tasks completed
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pending Tasks & Upcoming Visits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <Card data-testid="pending-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Pending Tasks</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {dashboardData?.pending_tasks?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.pending_tasks.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <CheckSquare className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-sm">{task.title}</p>
                        {task.due_date && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Due: {formatDate(task.due_date)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge className={getStatusColor(task.status)}>
                      {task.status?.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-muted-foreground">No pending tasks</p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Visits */}
        <Card data-testid="upcoming-visits-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Upcoming Visits</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/visits')}>
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {dashboardData?.upcoming_visits?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.upcoming_visits.slice(0, 5).map((visit) => (
                  <div
                    key={visit.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-primary" />
                      <div>
                        <p className="font-medium text-sm">{visit.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(visit.visit_date)}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(visit.status)}>
                      {visit.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-muted-foreground">No upcoming visits</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientDashboard;
